<template>
  <button
    v-if="!isUnavailable"
    :id="`add-to-list-${productId}`"
    class="add-to-list-icon button"
    aria-label="Add to wishlist"
    title="Add to wishlist"
    :disabled="isLoading"
    @click.stop="addToList"
    @keyup.enter="addToList"
  >
    <span
      v-if="!isLoading"
      :class="isMcom ? 'heart-thick-border' : 'heart-white-filled'"
    />
    <Spinner v-if="isLoading" />
  </button>
  <Toast
    v-if="state.listId.value || error"
    :toast-container-id="props.containerId"
    :placement="toastPlacement"
    :duration="2000"
  >
    <template
      v-if="state.listId.value"
    >
      <div>
        Added to <a :href="addToWishlistUrl">{{ state.listName.value }}</a>.
      </div>
    </template>
    <div
      v-if="error"
      class="error-message"
    >
      {{ error.message }}
    </div>
  </Toast>
</template>

<script setup>
/* istanbul ignore file */
// this is until wishlist issue for unavailable is resolved
import { computed, defineAsyncComponent, markRaw } from 'vue';
import Spinner from '@atomic-ui/spinner';
import Toast from '@atomic-ui/toast/Toast';
import { useAddToList } from '@composables/useAddToList/useAddToList';
import { AddToList } from '@composables/useAddToList/consts';
import { useDeviceType } from '@composables/utils/useDeviceType';
import { useSlideout } from '@composables/utils/useSlideout';
import { useBrand } from '@composables/useBrand';
import { useProductType } from '@composables/useProductType';
import { useFeatureEligibility } from '@modules/featureControl';
import { useProduct } from '@modules/product';

const props = defineProps({
  containerId: {
    type: String,
    required: true,
  },
});

const { productId, productData } = useProduct();
const { isMobile } = useDeviceType();
const toastPlacement = computed(() => (isMobile.value ? 'bottom' : 'right'));
const { showSlideout } = useSlideout();
const { isMcom } = useBrand();
const { isUnavailable } = useProductType();
const featureEligibility = useFeatureEligibility();
/**
 * @param { CustomerListResponse } CustomerList
 */
const slideoutOnSuccess = (CustomerList) => {
  showSlideout({
    id: `add-to-list-${productId}`,
    title: 'Add to list',
    component: markRaw(
      defineAsyncComponent(/* istanbul ignore next */ () => import(/* webpackChunkName: "ProductDetailsInfo" */ '@components/slideoutContent/addToListSlideout/AddToListInfo')),
    ),
    props: {
      product: productData,
      customerList: CustomerList,
      productId,
      maxLists: 5,
      featureEligibility,
    },
  });
};

const {
  state,
  isLoading,
  addToList,
  error,
} = useAddToList(productId, { slideoutOnSuccess }, featureEligibility);

const addToWishlistUrl = computed(() => `${AddToList.addToListPageUrl}?listGuid=${state.listId.value}`);

</script>

<style lang="scss" scoped>
@import "~settingSCSS";

.add-to-list-icon {
  position: absolute;
  right: 0;
  top: 0;
  z-index: 1;
}
.toast {
  position: absolute;
  @include margin-top-m;

  @include breakpoint(small only) {
    @include margin-bottom-xxxl;
  }
}
a {
  color: $white;
}
</style>

<style lang="scss" brand="mcom" scoped>
@import '@core/csg/src/scss/encodedIcons/mcom/_heart-thick-border.scss';
</style>

<style lang="scss" brand="bcom" scoped>
@import '@core/csg/src/scss/encodedIcons/bcom/_heart-white-filled.scss';
</style>
